import React, { useState } from 'react'
import { css } from '@emotion/core'
import { createPortal } from 'react-dom'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import { Separator, List, ListItem, Row, Card, H3, Subheading, Button, useContainerWidth, colors } from '@fs/zion-ui'
import { useTranslation, Trans } from 'react-i18next'
import { DocumentBasic, ControlDrag } from '@fs/zion-icon'
import { DndContext, DragOverlay } from '@dnd-kit/core'
import { getNestedOrFloatingElementById, getPersonName, useSlsStateContext } from '@fs/zion-sls'
import Draggable, { dashedBorderCssStyle } from '../../ui/Draggable'
import Droppable from '../../ui/Droppable'

const iconCss = css`
  padding: 12px 8px;
  cursor: grab;
  align-self: center;
`

const draggableStyle = {
  display: 'flex',
  backgroundColor: colors.gray00,
  borderRadius: '6px',
}

const dragHandleStyle = {
  display: 'flex',
  backgroundColor: colors.blue30,
  marginInlineStart: '12px',
  borderStartEndRadius: '6px',
  borderEndEndRadius: '6px',
}

const childrenStyle = {
  flexGrow: 1,
}

const flexItemCss = css`
  min-height: 120px;
  padding: 10px;
`

function DraggableRelationshipGroup({ name, otherName }) {
  return (
    <Draggable
      id="relationship"
      style={draggableStyle}
      dragHandleStyle={dragHandleStyle}
      childrenStyle={childrenStyle}
      dragHandle={
        <div css={iconCss}>
          <ControlDrag />
        </div>
      }
    >
      <List>
        <ListItem primaryText={name} startElement={<ListItem.Icon Icon={DocumentBasic} />} />
        <ListItem primaryText={otherName} startElement={<ListItem.Icon Icon={DocumentBasic} />} />
      </List>
    </Draggable>
  )
}

function DroppableRecordArea({ id, relationships, selectedRecord, name, otherName }) {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const [seeMore, setSeeMore] = useState(false)

  const relationshipsListCss = css`
    max-height: 140px;
    overflow-x: hidden;
    overflow-y: ${seeMore ? 'scroll' : 'hidden'};
    background-color: ${colors.gray00};
  `

  return (
    <Droppable id={id}>
      <div style={dashedBorderCssStyle} css={flexItemCss}>
        <H3 size="H6">
          {t('edit-relationships.choose-record-overlay.recordX', 'Record {number}', {
            number: id === 'current' ? '1' : '2',
          })}
          <Subheading>{t('edit-relationships.choose-record-overlay.move-here', 'Move Relationship Here')}</Subheading>
        </H3>
        <Separator size="xs" />
        {selectedRecord === id && <DraggableRelationshipGroup name={name} otherName={otherName} />}
        <Separator size="xs" />
        <div css={relationshipsListCss}>
          <List>
            {relationships.map((relationship) => {
              const relationshipPerson = getNestedOrFloatingElementById(state.images, relationship.otherPersonId)
              return (
                <ListItem
                  key={relationship.id}
                  overline={`${
                    relationshipPerson?.primary
                      ? `${t('edit-relationships.choose-record-overlay.principal', 'Principal')} | `
                      : ''
                  }${relationship.label}`}
                  primaryText={relationship.value}
                  startElement={<ListItem.Icon Icon={DocumentBasic} />}
                />
              )
            })}
          </List>
        </div>
        {relationships.length > 2 && (
          <Row alignX="center">
            <Button
              onClick={() => {
                setSeeMore(!seeMore)
              }}
            >
              {t('edit-relationships.choose-record-overlay.see-more.button', 'See More')}
            </Button>
          </Row>
        )}
      </div>
    </Droppable>
  )
}

export default function ChooseRecordOverlayStepOne({
  person,
  otherPerson,
  record1Relationships,
  record2Relationships,
  selectedRecord,
  setSelectedRecord,
}) {
  const [dragInfo, setDragInfo] = useState(false)
  const name = getPersonName({ person })
  const otherName = getPersonName({ person: otherPerson })
  const atWidth = useContainerWidth()

  return (
    <>
      <Trans i18nKey="edit-relationships.choose-record-overlay.drag-instructions">
        <strong>Drag</strong>
        <i> {{ name }}</i> and
        <i> {{ otherName }}</i> to the correct record. Record names have to be in the same record to have relationships.
      </Trans>
      <Separator size="xs" />
      <DndContext
        onDragStart={() => {
          setDragInfo(true)
        }}
        onDragEnd={(event) => {
          setDragInfo(false)
          setSelectedRecord(event.over.id)
        }}
      >
        <Card backgroundColor={colors.blue03}>
          <Row
            wrap={atWidth({
              default: true,
              sm: false,
            })}
          >
            <DroppableRecordArea
              growX
              relationships={record1Relationships}
              id="current"
              selectedRecord={selectedRecord}
              name={name}
              otherName={otherName}
            />
            <DroppableRecordArea
              growX
              relationships={record2Relationships}
              id="other"
              selectedRecord={selectedRecord}
              name={name}
              otherName={otherName}
            />
          </Row>
        </Card>
        {createPortal(
          <DragOverlay
            modifiers={[restrictToWindowEdges]}
            dropAnimation={null}
            style={{
              borderInlineStart: `4px solid${colors.blue10}`,
              boxShadow: '0 0 5px rgb(68 68 68 / 0.6)',
              background: 'white',
            }}
          >
            {dragInfo && <DraggableRelationshipGroup name={name} otherName={otherName} />}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </>
  )
}
